import type { PlaygroundNodeData } from "@/utils/playground/workflowToNodes";

import React from "react";
import { Handle, Position, type NodeProps } from "@xyflow/react";
import { Icon } from "@iconify/react";
import { Chip } from "@heroui/react";

import { useTheme } from "@/hooks/use-theme";

import { StatusBadge } from "./StatusBadge";

const PlaygroundInputNode: React.FC<NodeProps> = ({ data, selected }) => {
  const nodeData = data as PlaygroundNodeData;
  const { isDark } = useTheme();

  const paramCount = nodeData.inputParams ? Object.keys(nodeData.inputParams).length : 0;

  return (
    <div
      className={`
        px-4 py-3 rounded-xl border-2 min-w-[200px] transition-all duration-200
        ${selected ? "ring-2 ring-primary-400 ring-offset-2" : ""}
        ${isDark ? "bg-default-100 border-default-300" : "bg-white border-stone-300"}
        shadow-md hover:shadow-lg
      `}
    >
      <div className="flex items-center gap-3">
        <div
          className={`
            rounded-full w-10 h-10 flex items-center justify-center shrink-0
            ${isDark ? "bg-default-200" : "bg-stone-100"}
          `}
        >
          <Icon
            className={`w-5 h-5 ${isDark ? "text-default-600" : "text-stone-600"}`}
            icon="solar:document-text-linear"
          />
        </div>
        <div className="min-w-0">
          <div
            className={`text-sm font-semibold ${isDark ? "text-default-800" : "text-stone-800"}`}
          >
            {nodeData.label}
          </div>
          {paramCount > 0 && (
            <Chip className="mt-0.5" color="default" size="sm" variant="flat">
              {paramCount} {paramCount === 1 ? "param" : "params"}
            </Chip>
          )}
        </div>
      </div>

      {nodeData.status && (
        <div className="absolute -top-2 -right-2">
          <StatusBadge isCurrentStep={nodeData.isCurrentStep || false} status={nodeData.status} />
        </div>
      )}

      <Handle
        className="!w-3 !h-3 !bg-primary-500 !border-2 !border-white"
        position={Position.Right}
        type="source"
      />
    </div>
  );
};

export default PlaygroundInputNode;
